// Detail page — full sliders, scenario presets, charts, sensitivity, risk flags.
// State (inputs / scenario / persistence) lives in App.jsx; this page is a
// view + editor over those props. rowsByScenario is recomputed locally because
// CumCashChart overlays all three scenarios simultaneously.

import { useMemo, useState } from 'react';
import { runForecast, summarize } from '../../engine/index.js';
import ScenarioPills from '../components/ScenarioPills.jsx';
import CumCashChart from '../components/CumCashChart.jsx';
import RevenueCostStack from '../components/RevenueCostStack.jsx';
import TornadoChart from '../components/TornadoChart.jsx';
import RisksPanel from '../components/RisksPanel.jsx';
import VariableEditor from '../components/VariableEditor.jsx';
import BreakEvenRequirements from '../components/BreakEvenRequirements.jsx';
import StablecoinEconomics from '../components/StablecoinEconomics.jsx';
import PulsarDropsEconomics from '../components/PulsarDropsEconomics.jsx';
import AdvancedScenarioGraphics from '../components/AdvancedScenarioGraphics.jsx';
import WirexRailEconomics from '../components/WirexRailEconomics.jsx';
import ProfitabilityBridge from '../components/ProfitabilityBridge.jsx';
import { fmtCompactEur, fmtCompactUsers } from '../formatters.js';
import { MODEL_CHECKPOINTS } from '../modelSnapshot.js';

const SCENARIO_KEYS = ['conservative', 'base', 'bull'];

export default function DetailPage({
  inputs,
  setInputs,
  resetInputs,
  scenarioKey,
  setScenarioKey,
}) {
  const [editorOpen, setEditorOpen] = useState(false);

  const rowsByScenario = useMemo(
    () =>
      SCENARIO_KEYS.reduce((acc, k) => {
        acc[k] = runForecast(inputs, k);
        return acc;
      }, {}),
    [inputs],
  );

  const summary = useMemo(
    () => summarize(rowsByScenario[scenarioKey]),
    [rowsByScenario, scenarioKey],
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
        <div className="max-w-2xl">
          <p className="caption text-ice-700">Pulsar Economics — Advanced model</p>
          <h1 className="h2 mt-3">12-month plan and operating sensitivity.</h1>
          <p className="body text-carbon-500 mt-3">
            The headline KPIs come from the latest Pulsar Financial sheet. Use the scenario controls
            below to pressure-test the older simulator and operating assumptions.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ScenarioPills value={scenarioKey} onChange={setScenarioKey} />
          <button
            type="button"
            onClick={() => setEditorOpen(true)}
            className="h-9 px-4 rounded-full label bg-carbon-800 text-white hover:bg-carbon-900 transition-colors"
          >
            Edit assumptions
          </button>
        </div>
      </header>

      <div className="space-y-6">
        <KpiStrip scenarioKey={scenarioKey} />
        <AdvancedScenarioGraphics />
        <WirexRailEconomics inputs={inputs} rows={rowsByScenario[scenarioKey]} />
        <ProfitabilityBridge rows={rowsByScenario[scenarioKey]} summary={summary} />
        <CumCashChart rowsByScenario={rowsByScenario} active={scenarioKey} />

        <BreakEvenRequirements summary={summary} />
        <StablecoinEconomics inputs={inputs} summary={summary} />
        <PulsarDropsEconomics summary={summary} />

        <div className="grid gap-6 lg:grid-cols-2">
          <RevenueCostStack rows={rowsByScenario[scenarioKey]} />
          <RisksPanel summary={summary} />
        </div>

        <TornadoChart inputs={inputs} />
      </div>

      <VariableEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        inputs={inputs}
        onChange={setInputs}
        onResetAll={resetInputs}
      />
    </div>
  );
}

function KpiStrip({ scenarioKey }) {
  const { m12 } = MODEL_CHECKPOINTS;
  const cards = [
    {
      label: 'M12 cardholders',
      value: fmtCompactUsers(m12.cardholders),
      sub: 'Latest sheet target',
    },
    {
      label: 'M12 B2C ARR',
      value: fmtCompactEur(m12.b2cArr),
      sub: 'Annualized consumer run-rate',
    },
    {
      label: 'M12 total ARR',
      value: fmtCompactEur(m12.totalArr),
      sub: 'B2C plus B2B run-rate',
    },
    {
      label: 'M12 B2B ARR',
      value: fmtCompactEur(m12.b2bArr),
      sub: `${scenarioKey} sensitivity active below`,
    },
  ];

  return (
    <section className="grid gap-3 md:grid-cols-4">
      {cards.map((card) => (
        <div key={card.label} className="rounded-card border border-carbon-100 bg-white p-5">
          <p className="caption text-carbon-400">{card.label}</p>
          <p className="text-3xl font-semibold tracking-h2 num-tabular mt-3 leading-none">
            {card.value}
          </p>
          <p className="label text-carbon-500 mt-3">{card.sub}</p>
        </div>
      ))}
    </section>
  );
}
